import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import {
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
} from '@/constants/routes';
import {
  getResponsiveImage,
  useCloudflareImage,
} from '@/hooks/useCloudflareImage';
import { getOptions } from '@/hooks/useIndustryOptionsByGridRule';
import { ORDERED_INDUSTRIES } from '@/constants/industries';
import { useMarketOptions } from '@/hooks/discover/useMarketOptions';
import { getGridViewCoverFromImageUrl } from '@/utilities/coverImages';
import Breadcrumb from '@components/Breadcrumb';
import { ImageHeader } from '@components/Header';
import { Snowflake } from '@simplywallst/ui-charts/Snowflake';
import {
  Badge,
  Box,
  Button,
  Grid,
  Text,
  useTheme,
} from '@simplywallst/ui-core';
import { Heading } from '@simplywallst/ui-core/next';
import EditOutline from '@simplywallst/ui-core/icons/EditOutline';
import ListSolid from '@simplywallst/ui-core/icons/ListSolid';
import TilesSolid from '@simplywallst/ui-core/icons/TilesSolid';
import { useCallback } from 'react';
import { useTracking } from 'react-tracking';
import type { BasicHeadingProps } from './types';
import { StockAlertSubscription } from './components/StockAlertSubscription';
import { parseSnowflakePointsFromRules } from './components/NewBasicHeading/utils';
import { MarketIndustryFilter } from './components/MarketIndustryFilter';
import { RecentResultsToggle } from '@/pages/RecentResultsToggle';
import { getRecentResultsToggleText } from '@/pages/RecentResultsToggle/utils';
import StopSignSolid from '@simplywallst/ui-core/icons/StopSignSolid';
import { cn } from '@simplywallst/ui-core/next';

export const BasicHeading = ({
  description,
  pageDescription,
  id,
  name,
  imageUrl,
  onIndustryUpdate,
  onMarketUpdate,
  rules,
  selectedMarket,
  selectedIndustry,
  onlyMarketFiltered,
  onAdvancedFilterClick,
  stocksView,
  onViewSelect,
  totalRecords,
  newCompaniesCount,
  slug,
  returnRecentCompaniesOnly,
  onOnlyRecentSearchesUpdate,
  isLoading,
  hideMarketFilter,
  maxResultCount,
}: BasicHeadingProps) => {
  const { trackEvent } = useTracking();
  const theme = useTheme();

  const additionalLinks = [
    {
      name: 'Investing Ideas',
      dispatch: { type: ROUTE_DISCOVER_INVESTING_IDEAS },
    },
    {
      name,
    },
  ];

  const route =
    selectedIndustry !== 'any'
      ? ROUTE_INVESTING_IDEA_MARKET_INDUSTRY
      : ROUTE_INVESTING_IDEA_MARKET;
  const payload = {
    id: id || '0',
    name: slug || 'slug',
    industry: selectedIndustry,
  };

  const marketOptions = useMarketOptions(true, route, payload);

  const industryOptions = getOptions({
    market: selectedMarket,
    id,
    name: slug,
    industries: ORDERED_INDUSTRIES,
  });

  const coverImageSrc = useCloudflareImage({
    src: getGridViewCoverFromImageUrl(imageUrl),
    width: 1152,
    height: 168,
    fit: 'cover',
  });

  const { srcSet: coverImageSrcSet } = getResponsiveImage({
    src: getGridViewCoverFromImageUrl(imageUrl),
    breakpoints: [
      { width: 1152, height: 168 },
      { width: 976, height: 168 },
      { width: 768, height: 176, fit: 'contain' },
      { width: 425, height: 237, fit: 'contain' },
      { width: 375, height: 261, fit: 'contain' },
    ],
  });

  const handlerMarketChange = useCallback(
    (market: string) => {
      onMarketUpdate(market as PrimaryCountryISOElseGlobal);
    },
    [onMarketUpdate]
  );

  const handleTableView = useCallback(() => {
    onViewSelect('table');
    trackEvent({
      action: 'click',
      subject: 'table-view',
    });
  }, [onViewSelect, trackEvent]);

  const handleTileView = useCallback(() => {
    onViewSelect('tile');
    trackEvent({
      action: 'click',
      subject: 'tile-view',
    });
  }, [onViewSelect, trackEvent]);

  const isFilterAvailable = Boolean(
    maxResultCount && totalRecords < maxResultCount
  );
  const htmlFormatClasses = cn([
    'prose',
    'max-w-none',
    //text
    'text-p-white-130',
    'text-sm',
    'text-pretty',
    'prose-strong:text-p-white-130',
    'prose-code:text-p-white-130',
    'prose-blockquote:text-p-white-130',
    //paragraph
    'prose-p:text-sm',
    'prose-p:my-2',
    'prose-p:text-pretty',
    //heading
    'prose-h3:text-xl',
    'prose-h3:text-p-white-130',
    'prose-h3:mb-x1',
    'prose-h4:text-lg',
    'prose-h4:text-p-white-130',
    //link
    'prose-a:text-p-white-130',
    'prose-a:underline',
    //lists
    'prose-ul:my-2',
    'prose-ul:text-p-white-130',
    'prose-ol:my-2',
    'prose-ol:text-p-white-130',
    'prose-li:marker:text-p-white-130',
    'prose-li:text-pretty',
  ]);

  return (
    <>
      <ImageHeader
        imageSource={coverImageSrc}
        imageSourceSet={coverImageSrcSet}
      >
        <Grid
          templateColumns="3fr minmax(120px, 2fr)"
          templateRows="max-content repeat(6, max-content)"
          templateColumnsMd="200px 2fr auto auto"
          templateRowsMd="max-content repeat(4, auto)"
          gap="16px"
        >
          <Grid column="1" columnMd="2" alignItems="center" paddingLeftMd="x6">
            <Breadcrumb list={additionalLinks} />
          </Grid>
          <Grid column="2" columnMd="3" justifyContent="flex-end">
            <Button
              styleType="loud"
              alignIcon="left"
              onClick={onAdvancedFilterClick}
            >
              <EditOutline />
              Edit Screener
            </Button>
          </Grid>
          <Grid row="4" columnMd="4" rowMd="1" justifyContent="flex-start">
            <StockAlertSubscription
              rules={rules}
              name={name}
              description={description}
            />
          </Grid>
          <div className="col-[2] row-[4_/_span_3] md:col-[1] md:row-[2_/_span_5]">
            <Snowflake points={parseSnowflakePointsFromRules(rules)}>
              <Snowflake.Labels />
            </Snowflake>
          </div>
          <div className="col-span-2 row-[2] grid gap-2 text-pretty md:col-[2_/_span_3] md:row-[2] md:items-start md:pl-x6">
            <Heading as="h1" size="hero" data-cy-id="page-header-title">
              {name}
            </Heading>
          </div>
          <Grid
            row="3"
            column="span 2"
            rowMd="3"
            columnMd="2 / span 3"
            paddingLeftMd="x6"
            templateColumns="repeat(3, max-content)"
            alignItems="center"
            gap="4px"
          >
            <Text color="lightGray" typography="xSmall">
              Results
            </Text>

            <Text typography="xSmall">{totalRecords}</Text>

            <Badge>
              <Grid templateColumns="auto auto" alignItems="center" gap="4px">
                <Box
                  width="6px"
                  height="6px"
                  backgroundColor="attention"
                  borderRadius="x2"
                />
                <span>{newCompaniesCount ?? 0} new</span>
              </Grid>
            </Badge>
          </Grid>

          <div className="row-[5] items-start md:col-[2_/_span_3] md:row-[4] md:pl-x6">
            <div
              className={htmlFormatClasses}
              dangerouslySetInnerHTML={{
                __html: pageDescription || description,
              }}
            />
          </div>

          <Grid
            gap="8px"
            paddingLeftMd="x6"
            row="7"
            column="1 / span 2"
            rowMd="5"
            columnMd="2 / span 3"
            alignItemsMd="start"
            templateColumns="1fr"
            templateColumnsSm="1fr 1fr"
            templateColumnsMd="max-content max-content"
          >
            <MarketIndustryFilter
              marketOptions={marketOptions}
              industryOptions={industryOptions}
              selectedMarket={selectedMarket}
              selectedIndustry={selectedIndustry}
              hideIndustryFilter={onlyMarketFiltered}
              hideMarketFilter={hideMarketFilter}
              onMarketChange={handlerMarketChange}
              onIndustryChange={onIndustryUpdate}
              onAdvancedFilterClick={onAdvancedFilterClick}
            />
          </Grid>
        </Grid>
      </ImageHeader>
      <Grid
        paddingXLg="x0"
        paddingX="x3"
        paddingY="x2"
        templateColumns="max-content 1fr"
        templateRows="auto auto"
        templateRowsSm="auto"
        templateColumnsSm="max-content max-content 1fr"
        alignContent="center"
        alignItems="center"
        gap="8px"
      >
        <Text
          typography="xSmallStrong"
          color="textSolid"
          data-cy-id="total-company-count"
        >
          {totalRecords.toLocaleString()} companies
        </Text>

        <Grid
          row="2"
          rowSm="1"
          column="span 2"
          columnSm="2"
          justifyContent="flex-start"
        >
          {onOnlyRecentSearchesUpdate && (
            <RecentResultsToggle
              onChange={onOnlyRecentSearchesUpdate}
              value={returnRecentCompaniesOnly}
              isLoading={isLoading}
            >
              <Grid
                templateColumns="1fr max-content"
                alignItems="center"
                gap="6px"
              >
                {isFilterAvailable ? (
                  <>
                    <Box
                      width="6px"
                      height="6px"
                      backgroundColor="attention"
                      borderRadius="x2"
                    />
                    {getRecentResultsToggleText({
                      newCompaniesCount,
                    })}
                  </>
                ) : (
                  <>
                    <StopSignSolid
                      fill={
                        returnRecentCompaniesOnly
                          ? theme.color.brand01
                          : theme.color.darkGray
                      }
                    />
                    <Text color="textSoft" typography="tiny">
                      New companies only available when results are &lt;{' '}
                      {maxResultCount}
                    </Text>
                  </>
                )}
              </Grid>
            </RecentResultsToggle>
          )}
        </Grid>

        <Grid gap="8px" templateColumns="auto auto" justifyContent="flex-end">
          <Button
            data-cy-id="table-view"
            styleType={stocksView === 'tile' ? 'passiveLarge' : 'loudLarge'}
            marginRight
            onClick={handleTableView}
          >
            <ListSolid />
          </Button>
          <Button
            data-cy-id="tile-view"
            styleType={stocksView === 'table' ? 'passiveLarge' : 'loudLarge'}
            onClick={handleTileView}
          >
            <TilesSolid />
          </Button>
        </Grid>
      </Grid>
    </>
  );
};
